import { Dimensions } from 'react-native';
import { DateTime } from 'luxon';

const WINDOW_WIDTH = Dimensions.get('window').width;
const WINDOW_HEIGHT = Dimensions.get('window').height;

const _colorSet = {
  // Core theme colors
  mainThemeBackgroundColor: 'white',
  mainThemeForegroundColor: '#3068CC',
  mainTextColor: '#464646',
  mainSubtextColor: '#b6b9bf',
  hairlineColor: '#d6d6d6',
  whiteColor: 'white',

  // Brand colors
  brandPrimary: '#FFC451',
  brandPrimaryLight: 'rgba(255, 196, 81, 0.15)',
  brandPrimaryMedium: 'rgba(255, 196, 81, 0.3)',

  // V3 Design System Colors
  inputBackground: '#F5F5F5',
  inputBackgroundFocused: '#FFFFFF',
  inputBorder: '#E5E5E5',
  inputBorderFocused: '#FFC451',
  cardBackground: '#FFFFFF',
  cardBorder: '#F0F0F0',
  textPrimary: '#000000',
  textSecondary: '#6B7280',
  textMuted: '#9CA3AF',
  toggleTrackOff: '#E5E5E5',
  toggleTrackOn: '#FFC451',
  toggleThumb: '#FFFFFF',

  // Semantic colors (modern palette)
  success: '#22c55e',
  successLight: 'rgba(34, 197, 94, 0.15)',
  error: '#ef4444',
  errorLight: 'rgba(239, 68, 68, 0.15)',
  warning: '#f59e0b',
  warningLight: 'rgba(245, 158, 11, 0.15)',
  info: '#3b82f6',
  infoLight: 'rgba(59, 130, 246, 0.15)',

  // Status colors
  urgent: '#ef4444',
  closed: '#22c55e',
  open: '#f59e0b',
  pending: '#6b7280',

  // V3 Chart colors - unified data visualization palette
  chartBlue: '#3b82f6',       // Primary data series
  chartIndigo: '#6366f1',     // Secondary data series
  chartPurple: '#8b5cf6',     // Tertiary data series
  chartGreen: '#22c55e',      // Success/positive metrics
  chartAmber: '#f59e0b',      // Warning/attention metrics
  chartRed: '#ef4444',        // Error/urgent metrics
  chartGray: '#6b7280',       // Neutral/baseline data
  chartTeal: '#14b8a6',       // Alternative accent
  chartPink: '#ec4899',       // Additional accent

  // Legacy (keeping for compatibility)
  redColor: '#ef4444',
  analyticsColor: '#5f65fc',
  analyticsLightColor: '#5f65fc30',
  customersColor: '#ff8a02',
  customersLightColor: '#ff8a0230',
  ordersColor: '#e090d7',
  ordersLightColor: '#e090d730',
  tasksColor: '#6fb168',
  tasksLightColor: '#6fb16830',
  salesColor: '#f8d246',
  salesLightColor: '#f8d24630',
  productsColor: '#bc6e7e',
  productsLightColor: '#bc6e7e30',
  taskNotStartedColor: '#f8d246',
  taskDoneColor: '#22c55e',
};

const _fontSizeSet = {
  xxlarge: 40,
  xlarge: 30,
  large: 25,
  middle: 20,
  normal: 16,
  small: 13,
  xsmall: 11,
};

// Border radius scale - standardized values for consistency
const _borderRadii = {
  xs: 4,    // Small elements, checkboxes, progress bars
  sm: 8,    // Secondary elements, small buttons
  md: 12,   // Primary elements, buttons, inputs
  lg: 16,   // Cards, containers, modals
  pill: 20, // Pill chips, badges
  circle: 100, // Circular elements
};

// Spacing scale - standardized spacing values
const _spacing = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  xxl: 24,
  xxxl: 32,
};

// Border colors - consistent border styling
const _borderSet = {
  light: '#F0F0F0',   // Card borders, subtle dividers
  default: '#E5E7EB', // Input borders, chip borders
  dark: '#D1D5DB',    // Emphasized borders
};

// State colors - interaction feedback
const _stateSet = {
  disabled: 'rgba(255, 196, 81, 0.5)',  // Disabled primary elements
  disabledText: '#9CA3AF',               // Disabled text
  hover: '#F9FAFB',                       // Hover background
  pressed: '#F3F4F6',                     // Pressed background
  focus: '#FFC451',                       // Focus ring color
};

// Breakpoints for responsive design
const _breakpoints = {
  mobile: 480,
  tablet: 768,
  desktop: 1024,
  largeDesktop: 1440,
};

const _sizeSet = {
  buttonWidth: '70%',
  inputWidth: '80%',
  radius: 25,
};

const _iconSet = {
  home: require('./assets/icons/home.png'),
  bars: require('./assets/icons/bars.png'),
  bell: require('./assets/icons/bell.png'),
  feed: require('./assets/icons/feed.png'),
  logo: require('./assets/icons/logo.png'),
  orders: require('./assets/icons/orders.png'),
  analytics: require('./assets/icons/bars.png'),
  customers: require('./assets/icons/home.png'),
  tasks: require('./assets/icons/orders.png'),
  sales: require('./assets/icons/bell.png'),
  products: require('./assets/icons/feed.png'),
  logout: require('./assets/icons/logout.png'),
};

const _iconSizeSet = {
  large: 35,
  normal: 24,
  small: 20,
};

const _styleSet = {
  menuButton: {
    alignSelf: 'center',
    justifyContent: 'center',
    padding: 10,
    paddingLeft: 20,
    paddingRight: 20,
  },
  // Shadow presets
  shadowSmall: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  shadowMedium: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
  },
  shadowLarge: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.12,
    shadowRadius: 12,
    elevation: 5,
  },
  // Card style preset
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 8,
    elevation: 2,
    borderWidth: 1,
    borderColor: '#f0f0f0',
  },
  // V3 Component Presets
  buttonPrimaryV3: {
    backgroundColor: '#FFC451',
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonSecondaryV3: {
    backgroundColor: 'transparent',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: '#FFC451',
    paddingVertical: 14,
    paddingHorizontal: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  inputV3: {
    backgroundColor: '#F5F5F5',
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 16,
    fontSize: 16,
    color: '#000000',
  },
  inputV3Focused: {
    backgroundColor: '#FFFFFF',
    borderWidth: 2,
    borderColor: '#FFC451',
  },
  pillChip: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    backgroundColor: '#FFFFFF',
  },
  pillChipSelected: {
    backgroundColor: '#FFC451',
    borderColor: '#FFC451',
  },
  badge: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    backgroundColor: '#FFC451',
  },
};

const _functions = {
  timeFormat: (timeStamp) => {
    let time = '';
    if (timeStamp) {
      const dt = DateTime.fromMillis(timeStamp);
      if (DateTime.now().hasSame(dt, 'day')) {
        time = dt.toFormat('H:mm');
      } else {
        time = dt.toRelative();
      }
    }
    return time;
  },
};

const _chartConfigSingleLine = {
  backgroundGradientFrom: '#FFFFFF',
  backgroundGradientTo: '#FFFFFF',
  decimalPlaces: 0,
  paddingLeft: 0,
  color: () => `rgb(48, 104, 204)`,
};

const _chartConfigMultiLine = {
  backgroundGradientFrom: '#FFFFFF',
  backgroundGradientTo: '#FFFFFF',
  decimalPlaces: 0,
  paddingLeft: 0,
  color: () => `rgb(48, 104, 204)`,
};

const _dateRangeOptions = [
  { key: 'custom_range', label: 'Custom Range' },
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last_week', label: 'Last Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'year_to_date', label: 'Year to Date' },
  { key: 'lifetime', label: 'Lifetime' },
];

const _fontSet = {
  regular: 'AppleSDGothicNeo-Regular',
  bold: 'AppleSDGothicNeo-Bold',
};

const StyleDict = {
  colorSet: _colorSet,
  iconSet: _iconSet,
  iconSizeSet: _iconSizeSet,
  sizeSet: _sizeSet,
  fontSizeSet: _fontSizeSet,
  styleSet: _styleSet,
  borderRadii: _borderRadii,
  spacing: _spacing,
  borderSet: _borderSet,
  stateSet: _stateSet,
  breakpoints: _breakpoints,
  windowW: WINDOW_WIDTH,
  windowH: WINDOW_HEIGHT,
  utils: _functions,
  chartConfigSingleLine: _chartConfigSingleLine,
  chartConfigMultiLine: _chartConfigMultiLine,
  dateRangeOptions: _dateRangeOptions,
  dateFormat: 'YYYY-MM-DD',
  fontSet: _fontSet,
};

export default StyleDict;
